import type { RackConfigurationsSaveHandle } from './subsections/rackConfigurationsSaveHandle';
import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { SavedAttachment } from '../../../services/attachmentService';
import type { Request } from '../../../types';
import type { RackConfiguration } from '../../../types/rack';

export interface RackPowerInitialProps {
  formData: Record<string, unknown>;
  updateFormData: (
    field: string,
    value: string | number | boolean | RackConfiguration[],
  ) => void;
  requestId?: string;
  disabled?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  isEditing?: boolean;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  registerRackSaveHandle?: (
    handle: RackConfigurationsSaveHandle | null,
  ) => void;
  onRackEditorActiveChange?: (active: boolean) => void;
}

export interface RackPowerPersistedProps {
  request: Request;
  isEditing: boolean;
  editedData: Record<string, unknown>;
  updateEditedData: (field: string, value: string) => void;
  showFullDetails?: boolean;
  showViewToggle?: boolean;
  isAdvancedView?: boolean;
  onToggleView?: () => void;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  /**
   * Request Detail engine wiring (#997 Story 6.3 Phase 4b). The engine section
   * registers its save handle with the screen's `useRacksEngineSaveBridge` and
   * writes the live session list into `editedData.rackConfigurations` via
   * `applyEditedDataPatch` so it survives step navigation.
   */
  registerInstanceSaveHandle?: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  /** True when `editedData.rackConfigurations` was touched this edit session. */
  rackConfigurationsDirty?: boolean;
  onRackEditorActiveChange?: (active: boolean) => void;
}

export type RackPowerProps = RackPowerInitialProps | RackPowerPersistedProps;

export function isRackPowerPersistedProps(
  p: RackPowerProps,
): p is RackPowerPersistedProps {
  return 'request' in p && (p as RackPowerPersistedProps).request != null;
}
